/**
 * El badge de «Conversaciones» en el menú lateral: cuántas personas esperan respuesta con la ventana de 24 h
 * abierta («Por contestar»). Es el MISMO número que el total del filtro por defecto de la lista (§1.2.9):
 * el operador que ve «3» en el menú encuentra a esas 3 al entrar. Como el menú está en todas las pantallas
 * de Activa, el número se ve también desde Reactivación.
 *
 * Se pide al montar, cada 60 s y cuando alguien avisa un cambio (contestar, resolver, dar de baja). Mientras
 * carga, en 0 o con error no muestra nada: un badge viejo o inventado es peor que ninguno.
 *
 * Con error NO lanza, así `usePolling` nunca lo da por detenido y sigue preguntando cada 60 s. Las pantallas se
 * detienen para no esconder la falla detrás de números quietos, pero el badge ya se oculta con error y no avisa
 * «Dejamos de actualizar»: detenido, con el servidor de vuelta y gente esperando, la pestaña seguía sin número
 * hasta recargar (y un badge ausente se lee como «nadie espera»).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { conversacionesApi } from "../../conversacionesApi";
import type { ContadorConversaciones } from "../../types.conversaciones";
import { usePolling } from "../../hooks/usePolling";
import { llamar } from "../base/comun";
import { EVENTO_CONVERSACIONES_CAMBIO } from "../../logic/conversaciones";

/**
 * `compacto`: con el menú lateral colapsado (sólo íconos) el número no entra y va un punto sobre el ícono; el número
 * queda para el lector de pantalla. Es una prop y no otro componente para que expandir el menú no lo desmonte (y
 * vuelva a pedir el contador).
 */
export function ContadorSinResponder({ compacto = false }: { compacto?: boolean } = {}) {
  const [n, setN] = useState(0);
  const seq = useRef(0);
  const vivo = useRef(true);

  const cargar = useCallback(async () => {
    const mio = ++seq.current;
    const l = await llamar<ContadorConversaciones>(() => conversacionesApi.contador());
    if (!vivo.current || mio !== seq.current) return;
    if (!l.ok) { setN(0); return; }
    setN(Math.max(0, Number(l.data?.porContestar) || 0));
  }, []);

  useEffect(() => {
    vivo.current = true;
    void cargar().catch(() => undefined);
    const alCambiar = () => { void cargar().catch(() => undefined); };
    window.addEventListener(EVENTO_CONVERSACIONES_CAMBIO, alCambiar);
    return () => {
      vivo.current = false;
      window.removeEventListener(EVENTO_CONVERSACIONES_CAMBIO, alCambiar);
    };
  }, [cargar]);

  usePolling(cargar, 60_000, []);

  if (n <= 0) return null;
  if (compacto) {
    return (
      <>
        <span aria-hidden="true" className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white" />
        <span className="sr-only">({n} por contestar)</span>
      </>
    );
  }
  return (
    <>
      <span aria-hidden="true" className="ml-auto rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white tabular-nums">{n > 99 ? "99+" : n}</span>
      <span className="sr-only">({n} por contestar)</span>
    </>
  );
}

export default ContadorSinResponder;
